import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { resolveFeatureFlags, type FeatureFlagResolveResult } from '@/shared/data/featureFlags';
import { queryKeys } from '@/shared/hooks';

type FeatureFlagsCacheEntry = { flags: FeatureFlagResolveResult['flags']; keys: Set<string> };

const featureFlagsCache = new Map<string, FeatureFlagsCacheEntry>();
const shouldBypassFlags = import.meta.env.VITE_E2E_FLAGS_BYPASS === 'true';
const FEATURE_FLAGS_STORAGE_KEY = 'flowqa-feature-flags-cache';
const featureFlagsStorageTtlMs = 30 * 60_000;

type UseFeatureFlagsArgs = {
  keys: string[];
  token?: string | null;
  identityKey?: string | null;
  enabled?: boolean;
  refetchOnWindowFocus?: boolean;
  staleTimeMs?: number;
};

type StoredFeatureFlagsCacheEntry = {
  savedAt: number;
  flags: FeatureFlagResolveResult['flags'];
  keys: string[];
};

const readStoredFeatureFlagsCache = (cacheKey: string): StoredFeatureFlagsCacheEntry | null => {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<Record<string, StoredFeatureFlagsCacheEntry>>;
    const entry = parsed[cacheKey];
    if (!entry) return null;
    if (Date.now() - entry.savedAt > featureFlagsStorageTtlMs) {
      return null;
    }
    return entry;
  } catch {
    return null;
  }
};

const writeStoredFeatureFlagsCache = (
  cacheKey: string,
  entry: StoredFeatureFlagsCacheEntry,
): void => {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
    const parsed =
      raw !== null
        ? (JSON.parse(raw) as Partial<Record<string, StoredFeatureFlagsCacheEntry>>)
        : {};
    parsed[cacheKey] = entry;
    window.localStorage.setItem(FEATURE_FLAGS_STORAGE_KEY, JSON.stringify(parsed));
  } catch {
    // ignore storage failures
  }
};

export const useFeatureFlags = ({
  keys,
  token,
  identityKey,
  enabled = true,
  refetchOnWindowFocus = true,
  staleTimeMs,
}: UseFeatureFlagsArgs): {
  flags: FeatureFlagResolveResult['flags'] | null;
  isLoading: boolean;
} => {
  const bypassedFlags = useMemo(() => {
    if (!shouldBypassFlags) return null;
    const resolved: FeatureFlagResolveResult['flags'] = {};
    keys.forEach((key) => {
      resolved[key] = true;
    });
    return resolved;
  }, [keys]);
  if (bypassedFlags) {
    return { flags: bypassedFlags, isLoading: false };
  }
  const keysKey = useMemo(() => {
    if (keys.length === 0) return null;
    const sortedKeys = [...keys].sort();
    return JSON.stringify(sortedKeys);
  }, [keys]);
  const resolvedIdentityKey = identityKey ?? (token ? 'authenticated' : 'anonymous');
  const cacheKey = `global:${resolvedIdentityKey}`;
  const query = useQuery({
    queryKey: queryKeys.featureFlagResolve(keysKey, resolvedIdentityKey),
    queryFn: async () => {
      const cached = featureFlagsCache.get(cacheKey);
      if (cached) {
        const hasAllKeys = keys.every((key) => cached.keys.has(key));
        if (hasAllKeys) {
          return { flags: { ...cached.flags } };
        }
      }
      const result = await resolveFeatureFlags(keys, token);
      const existingFlags = cached?.flags ?? {};
      const mergedFlags = { ...existingFlags, ...result.flags };
      const mergedKeys = new Set(cached?.keys ?? []);
      keys.forEach((key) => mergedKeys.add(key));
      featureFlagsCache.set(cacheKey, { flags: mergedFlags, keys: mergedKeys });
      writeStoredFeatureFlagsCache(cacheKey, {
        savedAt: Date.now(),
        flags: mergedFlags,
        keys: [...mergedKeys],
      });
      return { flags: mergedFlags };
    },
    enabled: enabled && Boolean(token) && keys.length > 0,
    initialData: (() => {
      const inMemoryCached = featureFlagsCache.get(cacheKey);
      if (inMemoryCached) {
        const hasAllKeys = keys.every((key) => inMemoryCached.keys.has(key));
        if (hasAllKeys) {
          return { flags: { ...inMemoryCached.flags } };
        }
      }
      const stored = readStoredFeatureFlagsCache(cacheKey);
      if (!stored) return undefined;
      const storedKeys = new Set(stored.keys);
      const hasAllKeys = keys.every((key) => storedKeys.has(key));
      if (!hasAllKeys) return undefined;
      featureFlagsCache.set(cacheKey, {
        flags: stored.flags,
        keys: storedKeys,
      });
      return { flags: { ...stored.flags } };
    })(),
    refetchOnMount: false,
    refetchOnWindowFocus,
    staleTime: staleTimeMs ?? 30 * 60_000,
  });
  const flags = useMemo<FeatureFlagResolveResult['flags'] | null>(() => {
    if (!token) return null;
    if (query.isError) return null;
    return query.data?.flags ?? {};
  }, [query.data?.flags, query.isError, token]);

  return { flags, isLoading: query.isLoading };
};
